import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Divider,
  Menu,
  MenuItem,
  TextField,
  Typography,
} from '@material-ui/core';
import LockIcon from '@material-ui/icons/Lock';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import CreditCardIcon from '@material-ui/icons/CreditCard';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import ErrorOutlineIcon from '@material-ui/icons/ErrorOutline';
import SwapHorizIcon from '@material-ui/icons/SwapHoriz';

import withShipment from '../../withShipment';
import {
  userInfoSelector,
  isAuthenticatedSelector,
} from '../../redux/selectors/auth';
import { availableCompaniesSelector } from '../../redux/selectors/account';
import {
  authLogout,
  refreshUserInfo,
  updateUserInfo,
} from '../../redux/actions/auth';
import {
  activateLicenseKey,
  listAvailableCompanies,
} from '../../redux/actions/settings';
import PayWithStripeDialog from '../../components/stripe/PayWithStripeDialog';
import AuthUtil from '../../redux/AuthUtil';
import { STRIPE as STRIPE_ENDPOINTS } from '../../components/global/Server/endpoints';

// Reuse the exact same Logo SVG + navbar styles as the main Header so the
// LockedShell top nav is pixel-identical (size, glow filter, responsive
// font-size breakpoints) — including the SVG-component fill behavior that
// an <img> tag can't replicate.
import { ReactComponent as Logo } from '../../components/header/nube-data-black.svg';
import '../../components/header/Header.css';

import './LockedShell.css';

/**
 * Full-screen lockout that REPLACES the entire authenticated app shell
 * (header, sidebar, content) when userInfo.lockState.locked === true.
 *
 * The single source of truth is `lockState` from the server. It declares:
 *   - `reason`     — license-expired | trial-grace-expired | admin-suspended | closed
 *   - `terminal`   — true when no self-recovery path exists (closed)
 *   - `canActivate`— this user role can submit an activation key
 *   - `canPay`     — this user role can self-pay via Stripe
 *   - `title`, `message` — pre-formatted human strings
 *   - `companyName`, `expiryDate`, `amount`, `planName` — context for the UI
 *
 * Role behavior:
 *   - NDS staff (TECH_ADMIN, MASTER_ADMIN), Owner, Admin → see both cards
 *   - Line staff (ROLE_USER, feature roles) → "Contact your manager" only
 *
 * The shell intentionally has NO routing — it's a single page. The only
 * navigation is logout. This guarantees no feature route can be reached
 * by URL manipulation while locked, even if MenuItems.jsx hardening drifts.
 */
class LockedShell extends React.Component {
  state = {
    activationKey: '',
    activating: false,
    activationError: null,
    activationSuccessMessage: null,
    payDialogOpen: false,
    // Latched on once the Stripe poll returns activated:true. Keeps the
    // PayWithStripeDialog open so its "Payment successful" panel
    // renders for the user instead of vanishing the moment the poll
    // resolves. Cleared on dialog close, at which point we trigger the
    // canonical LockedShell exit.
    paySucceeded: false,
    switchMenuAnchor: null,
    // Plan + processing fee + total breakdown for the Pay Now summary, so the
    // "Amount Due" and button reflect the grossed-up total the card is charged.
    feeQuote: null,
  };

  // Fetch the plan/fee/total breakdown for the locked company so the pay card
  // shows what will actually be charged (plan + Stripe fee). Best-effort.
  fetchFeeQuote = (lockState) => {
    const companyId =
      lockState?.companyId || this.props.userInfo?.lockState?.companyId;
    const amount = lockState?.payableAmount ?? lockState?.amount;
    if (!companyId) return;
    AuthUtil.postRequest(STRIPE_ENDPOINTS.QUOTE_FEE, { companyId, amount })
      .then((res) => {
        if (this._mounted && res?.data?.success) {
          this.setState({ feeQuote: res.data });
        }
      })
      .catch(() => {
        /* non-fatal — summary falls back to the plan amount */
      });
  };

  // Linked-company users may land in LockedShell because the COMPANY
  // they switched into is expired, while their other linked company is
  // still active. Offer a one-click switch back from the locked shell
  // so they don't have to log out / log back in just to escape.
  handleOpenSwitchMenu = (event) => {
    this.setState({ switchMenuAnchor: event.currentTarget });
  };

  handleCloseSwitchMenu = () => {
    this.setState({ switchMenuAnchor: null });
  };

  handleSwitchToCompany = (company) => {
    this.setState({ switchMenuAnchor: null });
    if (!company || !company.companyId) return;
    localStorage.setItem('selectedCompanyId', company.companyId);
    // Mirror what the header CompanyPicker writes to redux so the
    // post-reload picker reflects the company we're actually on, not
    // the one we left. Without this, redux-persist rehydrates with the
    // previously-selected (locked) company's id and the picker shows
    // the wrong selection until the next manual change.
    if (this.props.userInfo) {
      const originalAuthorities = this.props.userInfo.authority || [];
      const originalRoutes = this.props.userInfo.routes || [];
      const companyAuthorities = (company.authorities || []).map(
        (item) => item.authority || item
      );
      const companyRoutes = company.routes || [];
      // Tech-admin / NDS master admin keep their cross-company powers
      // intact when switching — same rule the header CompanyPicker
      // applies. Everyone else adopts the destination company's
      // authorities/routes (falling back to current if the picker entry
      // didn't ship them).
      const isCrossCompanyAdmin =
        originalAuthorities.includes('ROLE_TECH_ADMIN') ||
        originalAuthorities.includes('ROLE_NUVE_MASTER_ADMIN');
      const authorityArray = isCrossCompanyAdmin
        ? originalAuthorities
        : companyAuthorities.length
        ? companyAuthorities
        : originalAuthorities;
      const routesArray = isCrossCompanyAdmin
        ? originalRoutes
        : companyRoutes.length
        ? companyRoutes
        : originalRoutes;
      this.props.updateUserInfo({
        ...this.props.userInfo,
        selectedLocationId: company.companyId,
        selectedLocationName:
          company.name || this.props.userInfo.selectedLocationName,
        planName: company.planName || this.props.userInfo.planName,
        authority: authorityArray,
        routes: routesArray,
      });
    }
    // Full reload so AuthUtil picks up the new header for every saga
    // bootstrap and the new userInfo lockState is computed against the
    // freshly-selected company.
    window.location.reload();
  };

  getSwitchableCompanies = () => {
    const { availableCompanies, userInfo } = this.props;
    if (!Array.isArray(availableCompanies) || availableCompanies.length === 0) {
      return [];
    }
    const currentId =
      userInfo?.lockState?.companyId || userInfo?.selectedLocationId;
    return availableCompanies.filter((c) => c && c.companyId !== currentId);
  };

  formatCurrency = (amount) => {
    const num = Number(amount);
    if (!Number.isFinite(num) || num <= 0) return null;
    return `$${num.toFixed(2)}`;
  };

  formatDate = (raw) => {
    if (!raw) return null;
    try {
      const d = new Date(raw);
      if (Number.isNaN(d.getTime())) return null;
      return d.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      });
    } catch (e) {
      return null;
    }
  };

  handleActivate = () => {
    const key = (this.state.activationKey || '').trim();
    if (!key || this.state.activating) return;
    this.setState({
      activating: true,
      activationError: null,
      activationSuccessMessage: null,
    });
    this.props.activateLicenseKey({ serialKey: key }, (success, message) => {
      if (!this._mounted) return;
      if (success === true) {
        this.setState({
          activating: false,
          activationSuccessMessage:
            message || 'Activation successful. Restoring access…',
        });
        // Old behavior was window.location.replace('/'), but with
        // redux-persist the reload rehydrated the stale lockState
        // and we'd flash LockedShell again until the 30s poll caught
        // up. Optimistic clear + refreshUserInfo gives an instant
        // transition: Base.jsx re-renders the moment lockState
        // becomes null (locally), and the saga's getUserInfo call
        // confirms / corrects the canonical state right after.
        this.exitLockedShell();
      } else {
        this.setState({
          activating: false,
          activationError:
            message ||
            'That activation key could not be verified. Please double-check and try again.',
        });
      }
    });
  };

  handlePaySuccess = () => {
    // DON'T close the dialog here — PayWithStripeDialog flips its
    // internal `done` flag in the same tick and renders the "Payment
    // successful" panel. Closing now would unmount it before the user
    // sees the confirmation. We latch paySucceeded so onClose can do
    // the LockedShell exit when the user dismisses the success panel.
    this.setState({ paySucceeded: true });
  };

  handlePayDialogClose = () => {
    const succeeded = this.state.paySucceeded;
    this.setState({ payDialogOpen: false, paySucceeded: false });
    if (succeeded) {
      // Same optimistic-then-canonical pattern as activation-by-key:
      // clear lockState in redux so Base.jsx unmounts LockedShell, then
      // refreshUserInfo overwrites the optimistic clear with the real
      // server state. Token stays valid (we don't wipe tokens for
      // billing-related locks).
      this.exitLockedShell();
    }
  };

  // Shared exit path: optimistically clear lockState in redux so Base.jsx
  // unmounts LockedShell immediately, then dispatch refreshUserInfo so
  // the canonical state from getUserInfo overwrites the optimistic clear.
  // Pre-existing token stays valid (we don't wipe tokens for billing-
  // related lock reasons), so no re-login required.
  exitLockedShell = () => {
    if (this.props.userInfo) {
      this.props.updateUserInfo({
        ...this.props.userInfo,
        lockState: null,
        // Trial-grace banner also clears since we're back to active.
        trialGrace: null,
      });
    }
    this.props.refreshUserInfo();
  };

  handleLogout = () => {
    this.props.authLogout();
  };

  componentDidMount() {
    this._mounted = true;
    // Immediate authoritative refresh on mount. The LockedShell can mount
    // from STALE redux-persist state — for example, after the user clicks
    // Switch Company (which rewrites localStorage.selectedCompanyId and
    // reloads the page), the boot state still has lockState from the
    // previous company. Without this kick, the user stays in LockedShell
    // for up to 30 seconds (the poll interval) before the picker takes
    // effect. Firing getUserInfo on mount sends X-Selected-Company =
    // the newly-selected company; the response either keeps the lock
    // (still on a locked company) or clears it (switched to active).
    try {
      this.props.refreshUserInfo();
    } catch (e) {
      // Best-effort — the 30s poll below picks up the slack.
    }
    // Quote the processing fee so the Pay card shows plan + fee + total.
    try {
      this.fetchFeeQuote(this.props.userInfo?.lockState);
    } catch (e) {
      /* non-fatal */
    }
    // Populate the Switch Company dropdown for multi-company users on a
    // fresh page load (redux-persist may not have cached the list, and
    // nothing else inside the locked shell triggers the fetch). The
    // /company/listAvailableCompanies endpoint is whitelisted in the
    // LockGateInterceptor so this call succeeds from a locked context.
    // Skipped when the list is already populated to avoid an unneeded
    // round-trip after every WS unlock / refresh cycle.
    if (
      !Array.isArray(this.props.availableCompanies) ||
      this.props.availableCompanies.length === 0
    ) {
      try {
        this.props.listAvailableCompanies();
      } catch (e) {
        // Best-effort — switcher just won't appear this session if the
        // dispatch is unavailable for some reason. Logging out + back in
        // is still a way out.
      }
    }
    // Safety net for admin-role users whose Base.jsx initConnections
    // skips WebSocket setup — they won't receive the 'company-unlocked'
    // broadcast when another admin pays from a different device. Without
    // this polling they'd be stranded in LockedShell until they took an
    // action that hit the network. 30s is cheap (one getUserInfo call)
    // and bounded to the locked-shell lifetime, so it stops cleanly when
    // the company unlocks and Base.jsx unmounts this component.
    this._pollInterval = setInterval(() => {
      if (!this._mounted) return;
      this.props.refreshUserInfo();
    }, 30000);
  }

  componentWillUnmount() {
    this._mounted = false;
    if (this._pollInterval) {
      clearInterval(this._pollInterval);
      this._pollInterval = null;
    }
  }

  renderHeader = () => {
    const { userInfo } = this.props;
    const fullName =
      [userInfo?.firstname, userInfo?.lastname].filter(Boolean).join(' ') ||
      userInfo?.username ||
      '';

    return (
      <nav className="navbar sticky-top navbar-dark locked-topnav">
        <div className="locked-topnav-left">
          {/* Brand block — same markup + class names as Header.jsx
              (navbar-brand / navbar-logo / brand-text) so the global
              Header.css rules drive sizing, the yellow drop-shadow,
              and the responsive font-size breakpoints. Rendered as a
              <span> (not <a>) because the LockedShell has no routing. */}
          <span className="navbar-brand locked-brand">
            <Logo className="navbar-logo" />
            <span className="brand-text">NuVe System</span>
          </span>
        </div>
        <div className="locked-topnav-right">
          {fullName && (
            <span className="locked-topnav-user-name">{fullName}</span>
          )}
          {this.renderSwitchCompany()}
          <Button
            onClick={this.handleLogout}
            startIcon={<ExitToAppIcon />}
            className="locked-topnav-logout"
          >
            Log out
          </Button>
        </div>
      </nav>
    );
  };

  renderSwitchCompany = () => {
    const switchable = this.getSwitchableCompanies();
    if (switchable.length === 0) return null;
    const { switchMenuAnchor } = this.state;
    return (
      <>
        <Button
          onClick={this.handleOpenSwitchMenu}
          startIcon={<SwapHorizIcon />}
          className="locked-topnav-logout"
          style={{ marginRight: 8 }}
        >
          Switch Company
        </Button>
        <Menu
          anchorEl={switchMenuAnchor}
          open={Boolean(switchMenuAnchor)}
          onClose={this.handleCloseSwitchMenu}
          getContentAnchorEl={null}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        >
          {switchable.map((c) => (
            <MenuItem
              key={c.companyId}
              onClick={() => this.handleSwitchToCompany(c)}
            >
              {c.name || c.companyId}
              {c.companyCode ? ` (${c.companyCode})` : ''}
            </MenuItem>
          ))}
        </Menu>
      </>
    );
  };

  // Announcement bar — sits directly under the navbar and surfaces the
  // company name + lock status as a persistent strip. Mirrors the
  // visual pattern of the trial-grace banner in Base.jsx so the locked
  // state feels like part of the same family of system notifications.
  renderAnnouncement = (lockState) => {
    const name = lockState.companyName || 'Your company';
    const code = lockState.companyCode;
    const label = code ? `${name} (${code})` : name;
    return (
      <div className="locked-announcement" role="status">
        <span className="locked-announcement-company">{label}</span>
        <span className="locked-announcement-sep" aria-hidden>
          ·
        </span>
        <span className="locked-announcement-pill">
          <LockIcon fontSize="inherit" /> Account Locked
        </span>
      </div>
    );
  };

  renderStatusCard = (lockState) => {
    const expiry = this.formatDate(lockState.expiryDate);
    const amount = this.formatCurrency(lockState.amount);
    return (
      <div className="locked-card locked-status-card">
        <div className="locked-status-card-top">
          <div className="locked-status-icon">
            <ErrorOutlineIcon fontSize="inherit" />
          </div>
          <div className="locked-status-text">
            <Typography variant="h5" className="locked-status-title">
              {lockState.title || 'Account Locked'}
            </Typography>
            <Typography variant="body1" className="locked-status-message">
              {lockState.message}
            </Typography>
          </div>
        </div>

        {(expiry || amount || lockState.planName) && (
          <>
            <Divider className="locked-divider" />
            <div className="locked-meta-grid">
              {lockState.planName && (
                <div className="locked-meta-item">
                  <span className="locked-meta-label">Plan</span>
                  <span className="locked-meta-value">
                    {lockState.planName}
                  </span>
                </div>
              )}
              {expiry && (
                <div className="locked-meta-item">
                  <span className="locked-meta-label">Expired On</span>
                  <span className="locked-meta-value">{expiry}</span>
                </div>
              )}
              {amount && (
                <div className="locked-meta-item">
                  <span className="locked-meta-label">Amount Due</span>
                  <span className="locked-meta-value">{amount}</span>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    );
  };

  renderActivationCard = () => {
    const {
      activationKey,
      activating,
      activationError,
      activationSuccessMessage,
    } = this.state;
    const trimmed = (activationKey || '').trim();

    return (
      <div className="locked-card locked-action-card">
        <div className="locked-action-header">
          <VpnKeyIcon className="locked-action-icon" />
          <div>
            <Typography variant="subtitle1" className="locked-action-title">
              Activate with License Key
            </Typography>
            <Typography variant="body2" className="locked-action-subtitle">
              If support has provided an activation key, enter it below to
              restore access immediately.
            </Typography>
          </div>
        </div>
        <Box mt={2}>
          <TextField
            label="License Key"
            variant="outlined"
            fullWidth
            value={activationKey}
            disabled={activating || !!activationSuccessMessage}
            onChange={(e) => this.setState({ activationKey: e.target.value })}
            // Pressing Enter submits, since it's the most common path
            // when an admin pastes a key.
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                trimmed &&
                !activating &&
                !activationSuccessMessage
              ) {
                e.preventDefault();
                this.handleActivate();
              }
            }}
            inputProps={{
              autoCapitalize: 'characters',
              autoCorrect: 'off',
              spellCheck: 'false',
              style: { letterSpacing: 1, fontFamily: 'Menlo, monospace' },
            }}
          />
        </Box>
        {activationError && (
          <Typography
            variant="body2"
            className="locked-inline-error"
            role="alert"
          >
            {activationError}
          </Typography>
        )}
        {activationSuccessMessage && (
          <Typography
            variant="body2"
            className="locked-inline-success"
            role="status"
          >
            {activationSuccessMessage}
          </Typography>
        )}
        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            onClick={this.handleActivate}
            disabled={!trimmed || activating || !!activationSuccessMessage}
            className="locked-primary-btn"
            startIcon={
              activating ? (
                <CircularProgress size={16} thickness={6} color="inherit" />
              ) : null
            }
          >
            {activating ? 'Activating…' : 'Activate Account'}
          </Button>
        </Box>
      </div>
    );
  };

  renderPayCard = (lockState) => {
    // Prefer the server-computed payableAmount (handles trial fallback
    // to plan price + latest-payment-row lookup). Fall back to the raw
    // license amount for older payloads that predate the field.
    const planLabel =
      this.formatCurrency(lockState.payableAmount) ||
      this.formatCurrency(lockState.amount);
    // When the fee quote is in, "Amount Due" + the button show the grossed-up
    // total (plan + processing fee) the card is actually charged.
    const { feeQuote } = this.state;
    const hasFee =
      feeQuote?.feeUSD != null &&
      Number(feeQuote.feeUSD) > 0 &&
      feeQuote?.totalUSD != null;
    const amountLabel = hasFee
      ? this.formatCurrency(feeQuote.totalUSD)
      : planLabel;

    return (
      <div className="locked-card locked-action-card">
        <div className="locked-action-header">
          <CreditCardIcon className="locked-action-icon" />
          <div>
            <Typography variant="subtitle1" className="locked-action-title">
              Pay Now
            </Typography>
            <Typography variant="body2" className="locked-action-subtitle">
              Pay securely with your card to immediately restore access.
            </Typography>
          </div>
        </div>

        {/* Payment summary — amount + plan + cycle so the customer
            knows exactly what they're being charged for. Only renders
            when we have a concrete amount; falls back to a plain
            "Pay Now" button otherwise. */}
        {amountLabel && (
          <div className="locked-pay-summary">
            {hasFee && (
              <>
                <div className="locked-pay-summary-row">
                  <span className="locked-pay-summary-label">Plan</span>
                  <span className="locked-pay-summary-value-sub">
                    {this.formatCurrency(feeQuote.baseUSD)}
                  </span>
                </div>
                <div className="locked-pay-summary-row">
                  <span className="locked-pay-summary-label">
                    Processing fee
                  </span>
                  <span className="locked-pay-summary-value-sub">
                    {this.formatCurrency(feeQuote.feeUSD)}
                  </span>
                </div>
              </>
            )}
            <div className="locked-pay-summary-row locked-pay-summary-total">
              <span className="locked-pay-summary-label">
                {hasFee ? 'Total' : 'Amount Due'}
              </span>
              <span className="locked-pay-summary-value">{amountLabel}</span>
            </div>
            {lockState.planName && (
              <div className="locked-pay-summary-row">
                <span className="locked-pay-summary-label">Plan</span>
                <span className="locked-pay-summary-value-sub">
                  {lockState.planName}
                </span>
              </div>
            )}
            {lockState.cycleLabel && (
              <div className="locked-pay-summary-row">
                <span className="locked-pay-summary-label">Billing Cycle</span>
                <span className="locked-pay-summary-value-sub">
                  {lockState.cycleLabel}
                </span>
              </div>
            )}
          </div>
        )}

        <Box mt={2} display="flex" justifyContent="flex-end">
          <Button
            variant="contained"
            color="primary"
            className="locked-primary-btn"
            onClick={() => this.setState({ payDialogOpen: true })}
          >
            {amountLabel ? `Pay ${amountLabel}` : 'Pay Now'}
          </Button>
        </Box>
      </div>
    );
  };

  renderContactCard = () => (
    <div className="locked-card locked-contact-card">
      <Typography variant="subtitle1" className="locked-action-title">
        Need help?
      </Typography>
      <Typography variant="body2" className="locked-action-subtitle">
        Please contact your manager or company owner to restore access. You will
        not be able to use the application until billing is resolved.
      </Typography>
    </div>
  );

  render() {
    const { userInfo } = this.props;
    const lockState = userInfo?.lockState;
    if (!lockState?.locked) return null;

    const showActivation = !!lockState.canActivate && !lockState.terminal;
    const showPay = !!lockState.canPay && !lockState.terminal;
    const showContactOnly = !showActivation && !showPay;

    return (
      <div className="locked-shell">
        {this.renderHeader()}
        {this.renderAnnouncement(lockState)}

        <main className="locked-main">
          <div className="locked-container">
            {this.renderStatusCard(lockState)}

            {showContactOnly && this.renderContactCard()}

            {(showActivation || showPay) && (
              <div className="locked-actions-grid">
                {showPay && this.renderPayCard(lockState)}
                {showActivation && this.renderActivationCard()}
              </div>
            )}
          </div>
        </main>

        {showPay && (
          <PayWithStripeDialog
            open={this.state.payDialogOpen}
            mode="pay_and_activate"
            companyId={lockState.companyId}
            amount={lockState.amount}
            requiresActivation
            onClose={this.handlePayDialogClose}
            onSuccess={this.handlePaySuccess}
          />
        )}
      </div>
    );
  }
}

LockedShell.propTypes = {
  userInfo: PropTypes.object,
  availableCompanies: PropTypes.array,
  activateLicenseKey: PropTypes.func.isRequired,
  authLogout: PropTypes.func.isRequired,
  refreshUserInfo: PropTypes.func.isRequired,
  updateUserInfo: PropTypes.func.isRequired,
  listAvailableCompanies: PropTypes.func.isRequired,
};

const mapStateToProps = (state) => ({
  userInfo: userInfoSelector(state),
  isAuthenticated: isAuthenticatedSelector(state),
  availableCompanies: availableCompaniesSelector(state),
});

const actionCreators = {
  activateLicenseKey,
  authLogout,
  refreshUserInfo,
  updateUserInfo,
  listAvailableCompanies,
};

export default withShipment(
  {
    mapStateToProps,
    actionCreators,
  },
  LockedShell
);
